import { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
	uploadDocument,
	uploadVersion,
} from 'slices/documentLibrarySlice'
import type {
	DocumentLibraryState,
	LibraryScopeArgs,
} from 'features/DocumentLibrary/types/documentLibrary.types'

let progressKeyCounter = 0
const nextProgressKey = (): string =>
	`upload-${Date.now()}-${++progressKeyCounter}`

interface UseDocumentUploadResult {
	uploadOne: (args: {
		scopeArgs: LibraryScopeArgs
		file: File
		libraryName?: string
		changeNote?: string
	}) => Promise<unknown>
	uploadNewVersion: (args: {
		id: string
		file: File
		changeNote: string
	}) => Promise<unknown>
	progressByKey: Record<string, number>
	isUploading: boolean
}

/**
 * Wraps the upload thunks with per-call progress keys and a derived
 * `isUploading` flag so the upload dialog doesn't have to manage that
 * state itself.
 */
export const useDocumentUpload = (): UseDocumentUploadResult => {
	const dispatch = useDispatch<any>()
	const progressByKey = useSelector(
		(state: { libraryDocuments: DocumentLibraryState }) =>
			state.libraryDocuments.uploadProgress,
	)
	const isUploading = Object.keys(progressByKey).length > 0

	const uploadOne = useCallback(
		(args: {
			scopeArgs: LibraryScopeArgs
			file: File
			libraryName?: string
			changeNote?: string
		}) => {
			const progressKey = nextProgressKey()
			return dispatch(uploadDocument({ ...args, progressKey })).unwrap()
		},
		[dispatch],
	)

	const uploadNewVersion = useCallback(
		(args: { id: string; file: File; changeNote: string }) => {
			const progressKey = nextProgressKey()
			return dispatch(uploadVersion({ ...args, progressKey })).unwrap()
		},
		[dispatch],
	)

	return { uploadOne, uploadNewVersion, progressByKey, isUploading }
}
